import React, {useCallback} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Flex, Heading, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage, useIntl, defineMessages} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useHistory} from 'react-router-dom'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    CrossCircle,
    EditPencil,
    InfoCircleFilled,
    TrackOrder,
    ReturnOrder,
    AlertIconCustom
} from '../../../components/icons'
import DSSActionButton from './dss-action-button'
import DSSActionDetails from './dss-action-details'
import {useDSSOrderDetail} from '../../../hooks/use-dss-order-detail'
import {DSS} from '../constant'
import {DSS_ACTION_BUTTONS, getDSSActionConfig} from './dss-action-config'

const {app: appConfig} = getConfig()
const staticResources = defineMessages({
    actionTitle: {
        id: 'account_order_detail.label.order.Action_Title',
        defaultMessage: 'Actions'
    },
    orderPrepared: {
        id: 'account_order_detail.action.text.being_prepared',
        defaultMessage: "Get excited, we're preparing your order!"
    },
    orderDeliveredGreeting: {
        id: 'account_order_detail.action.text.delivered_greetings',
        defaultMessage: 'Delivered, thanks for shopping with us!'
    },
    orderDeliveredReturnPeriodEnded: {
        id: 'account_order_detail.action.text.delivered_return_period',
        defaultMessage: 'Delivered, return period has ended'
    },
    orderWasCancelledAlert: {
        id: 'account_order_detail.action.text.was_cancelled_alert',
        defaultMessage: 'This order was cancelled'
    },
    partiallyCancelledBySystemAlert: {
        id: 'account_order_detail.action.text.partially_cancelled_by_system_alert',
        defaultMessage: 'Part of this order was cancelled'
    },
    partiallyCancelledByCustomerAlert: {
        id: 'account_order_detail.action.text.partially_cancelled_by_customer_alert',
        defaultMessage: 'You cancelled part of this order'
    },
    pendingReturnAlert: {
        id: 'account_order_detail.action.text.pending_return_alert',
        defaultMessage: 'Your return request has been received'
    },
    partiallyReturnedAlert: {
        id: 'account_order_detail.action.text.partially_returned_alert',
        defaultMessage: 'Refund Processed'
    },
    refundedAlert: {
        id: 'account_order_detail.action.text.refunded_alert',
        defaultMessage: 'Refund Processed'
    },
    cancelOrderButton: {
        id: 'account_order_detail.cancel_order_button',
        defaultMessage: 'Cancel order'
    },
    updateShippingAddressButton: {
        id: 'account_order_detail.update_shipping_address_button',
        defaultMessage: 'Update Shipping Address'
    },
    trackOrderButton: {
        id: 'account_order_detail.order_track_button',
        defaultMessage: 'Track Order'
    },
    returnOrderButton: {
        id: 'account_order_detail.return_order_button',
        defaultMessage: 'Return Order'
    }
})

const MESSAGE_CONFIG = {
    processing: {
        icon: InfoCircleFilled,
        message: staticResources.orderPrepared,
        testId: 'order-details-status-processing'
    },
    delivered: {
        icon: InfoCircleFilled,
        message: staticResources.orderDeliveredGreeting
    },
    deliveredReturnClosed: {
        icon: InfoCircleFilled,
        message: staticResources.orderDeliveredReturnPeriodEnded
    },
    cancelled: {
        icon: AlertIconCustom,
        message: staticResources.orderWasCancelledAlert,
        iconSize: '20px'
    },
    partiallyCancelledBySystem: {
        icon: AlertIconCustom,
        message: staticResources.partiallyCancelledBySystemAlert,
        iconSize: '20px'
    },
    partiallyCancelledByCustomer: {
        icon: AlertIconCustom,
        message: staticResources.partiallyCancelledByCustomerAlert,
        iconSize: '20px'
    },
    pendingReturn: {
        icon: InfoCircleFilled,
        message: staticResources.pendingReturnAlert,
        iconSize: '20px'
    },
    partiallyReturned: {
        icon: InfoCircleFilled,
        message: staticResources.partiallyReturnedAlert,
        iconSize: '20px'
    },
    refunded: {
        icon: InfoCircleFilled,
        message: staticResources.refundedAlert,
        iconSize: '20px'
    },
    bopisProcessing: {
        icon: InfoCircleFilled,
        message: staticResources.orderPrepared,
    },
    partiallyPendingReturn: {
        icon: InfoCircleFilled,
        message: staticResources.pendingReturnAlert,
        iconSize: '20px'
    },
}

const BUTTON_CONFIG = {
    [DSS_ACTION_BUTTONS.CANCEL]: {
        label: staticResources.cancelOrderButton,
        icon: <CrossCircle boxSize="16px" />,
        testId: 'order-details-button-cancel'
    },
    [DSS_ACTION_BUTTONS.UPDATE_SHIPPING]: {
        label: staticResources.updateShippingAddressButton,
        icon: <EditPencil boxSize="20px" />,
        testId: 'order-details-button-update-shipping'
    },
    [DSS_ACTION_BUTTONS.TRACK]: {
        label: staticResources.trackOrderButton,
        icon: <TrackOrder boxSize="20px" />,
        testId: 'order-details-button-track'
    },
    [DSS_ACTION_BUTTONS.RETURN]: {
        label: staticResources.returnOrderButton,
        icon: <ReturnOrder boxSize="20px" />,
        testId: 'order-details-button-return'
    }
}

// Temporary local toggles for UI testing.
const LOCAL_DSS_CTA_FEATURE_FLAGS = {
    [DSS_ACTION_BUTTONS.CANCEL]: appConfig.IS_ENABLE_DSSORDER_CANCELLATION === 'true',
    [DSS_ACTION_BUTTONS.UPDATE_SHIPPING]: appConfig.IS_ENABLE_DSSADDRESS_MODIFICATION === 'true',
    [DSS_ACTION_BUTTONS.TRACK]: appConfig.IS_ENABLE_DSSORDER_TRACKING === 'true',
    [DSS_ACTION_BUTTONS.RETURN]: appConfig.IS_ENABLE_DSSRETURNS === 'true'
}

const DSS_ACTION_BY_BUTTON = {
    [DSS_ACTION_BUTTONS.CANCEL]: DSS.ACTIONS.CANCELLATION,
    [DSS_ACTION_BUTTONS.UPDATE_SHIPPING]: DSS.ACTIONS.CHANGE_ADDRESS,
    [DSS_ACTION_BUTTONS.TRACK]: DSS.ACTIONS.TRACKING,
    [DSS_ACTION_BUTTONS.RETURN]: DSS.ACTIONS.RETURNS
}

const isButtonEligible = (buttonKey, order) => {
    const actionsCTA = order?.actionsCTA || {}

    if (
        buttonKey === DSS_ACTION_BUTTONS.CANCEL ||
        buttonKey === DSS_ACTION_BUTTONS.UPDATE_SHIPPING
    ) {
        return actionsCTA.isOrderEligibleForModification === true
    }

    if (buttonKey === DSS_ACTION_BUTTONS.RETURN) {
        return actionsCTA.isReturnEligible === true
    }

    if (buttonKey === DSS_ACTION_BUTTONS.TRACK) {
        return actionsCTA.isEligibleForTracking === true
    }

    return true
}

const getDescriptionKey = (descriptionKey, enabledButtons) => {
    if (descriptionKey !== 'received') {
        return descriptionKey
    }

    const canCancel = enabledButtons.includes(DSS_ACTION_BUTTONS.CANCEL)
    const canUpdateShipping = enabledButtons.includes(DSS_ACTION_BUTTONS.UPDATE_SHIPPING)

    if (canCancel && canUpdateShipping) {
        return 'received'
    }

    if (canCancel) {
        return 'receivedCancelOnly'
    }

    if (canUpdateShipping) {
        return 'receivedUpdateShippingOnly'
    }

    return null
}

const getVisibleDescriptionKey = (actionConfig, enabledButtons) => {
    const hasConfiguredButtons = actionConfig.buttons.length > 0
    const hasEnabledButtons = enabledButtons.length > 0

    if (hasConfiguredButtons && !hasEnabledButtons) {
        return null
    }

    return getDescriptionKey(actionConfig.description, enabledButtons)
}

const getTrackingInfo = (dssOrderDetails) => {
    const dssDetails = dssOrderDetails?.dssOrderDetails
    if (!dssOrderDetails?.dssOrderDetails) {
        return {
            trackingNumber: '',
            carrierCode: ''
        }
    }

    const orderTracking = dssDetails?.OrderTrackingInfo?.[0]

    if (orderTracking?.TrackingNumber && orderTracking?.CarrierCode) {
        return {
            trackingNumber: orderTracking.TrackingNumber,
            carrierCode: orderTracking.CarrierCode
        }
    }

    const fulfillmentWithTracking = dssDetails?.OrderLine?.[0]?.FulfillmentDetail?.find(
        ({ TrackingNumber, CarrierCode } = {}) => TrackingNumber && CarrierCode
    )

    return {
        trackingNumber: fulfillmentWithTracking?.TrackingNumber || '',
        carrierCode: fulfillmentWithTracking?.CarrierCode || ''
    }
}

const DSSActionMessage = ({messageKey, styles}) => {
    const intl = useIntl()
    const config = MESSAGE_CONFIG[messageKey]

    if (!config) {
        return null
    }

    const Icon = config.icon
    return (
        <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
            <Icon boxSize={config.iconSize || '16px'} mt="2px" />
            <Text data-testid={config.testId} {...styles.notifyText}>
                {intl.formatMessage(config.message)}
            </Text>
        </Box>
    )
}

const onClient = typeof window !== 'undefined'

export const DSSActions = ({order} = {}) => {
    const styles = useMultiStyleConfig('OrderDetailsDSS')
    const intl = useIntl()
    const history = useHistory()
    const locale = intl.locale
    const {data: customer} = useCurrentCustomer()
    const actionConfig = getDSSActionConfig(order)
    const orderObj = order || {}
    const {data: dssOrderDetails} = useDSSOrderDetail(orderObj.orderNo, {
        enabled: onClient && !!orderObj.orderNo && !!actionConfig.state
    })
    const orderEmail = orderObj?.account?.email || ''
    const billingPostcode = orderObj?.billingAddress?.postalCode || ''
    const isGuest = customer?.isGuest || false

    if (!actionConfig.state) {
        return null
    }

    const {orderNo, orderId, dssOmniDomain} = orderObj
    const enabledButtons = actionConfig.buttons.filter(
        (buttonKey) =>
            isButtonEligible(buttonKey, orderObj) && Boolean(LOCAL_DSS_CTA_FEATURE_FLAGS[buttonKey])
    )
    const descriptionKey = getVisibleDescriptionKey(actionConfig, enabledButtons)
    const hasVisibleContent =
        Boolean(actionConfig.message) || Boolean(descriptionKey) || enabledButtons.length > 0

    const handleDSSCTAClick = useCallback(
        (action) => {
            const token = dssOrderDetails?.dssOrderDetails?.OrderToken
            const dssOrderDetailsError = dssOrderDetails?.success === false
            const {trackingNumber, carrierCode} = getTrackingInfo(dssOrderDetails)

            history.push(`/dss-order-action-page/${orderNo}`, {
                token,
                orderNo,
                locale,
                dssOmniDomain,
                action,
                dssOrderDetailsError,
                trackingNumber,
                carrierCode,
                isGuest,
                orderEmail,
                billingPostcode
            })
        },
        [dssOrderDetails, history, orderId, orderNo, locale, dssOmniDomain, isGuest, orderEmail, billingPostcode]
    )

    if (!hasVisibleContent) {
        return null
    }

    return (
        <Stack>
            <Box {...styles.dssActionSection} data-testid="order-details-actions-section">
                <Flex flexDirection="column" gap="16px">
                    <Heading as="h2" {...styles.dssActionHeading}>
                        {intl.formatMessage(staticResources.actionTitle)}
                    </Heading>
                    <Box display="flex" flexDirection="column" gap="12px">
                        <DSSActionMessage messageKey={actionConfig.message} styles={styles} />
                        {descriptionKey && (
                            <DSSActionDetails descriptionKey={descriptionKey} styles={styles} />
                        )}
                    </Box>
                </Flex>
                <Flex w="full" flexDirection="column" gap="12px">
                    {enabledButtons.map((buttonKey) => {
                        const button = BUTTON_CONFIG[buttonKey]

                        return (
                            <DSSActionButton
                                key={buttonKey}
                                label={<FormattedMessage {...button.label} />}
                                icon={button.icon}
                                testId={button.testId}
                                onClick={() => handleDSSCTAClick(DSS_ACTION_BY_BUTTON[buttonKey])}
                            />
                        )
                    })}
                </Flex>
            </Box>
        </Stack>
    )
}

DSSActionMessage.propTypes = {
    messageKey: PropTypes.string,
    styles: PropTypes.object.isRequired
}

DSSActions.propTypes = {
    order: PropTypes.shape({
        statusRaw: PropTypes.string,
        orderStatus: PropTypes.string,
        orderNo: PropTypes.string,
        orderId: PropTypes.string,
        dssOmniDomain: PropTypes.string,
        account: PropTypes.shape({
            email: PropTypes.string
        }),
        billingAddress: PropTypes.shape({
            postalCode: PropTypes.string
        }),
        actionsCTA: PropTypes.shape({
            isOrderEligibleForModification: PropTypes.bool,
            isReturnEligible: PropTypes.bool,
            isDeliveredFinalSale: PropTypes.bool,
            isDeliveredReturnPeriodEnded: PropTypes.bool,
            allItemsEGiftCard: PropTypes.bool
        })
    })
}
